(() => {
  if (customElements.get("curated-collection")) {
    return;
  }

  class CuratedCollection extends HTMLElement {
    constructor() {
      super();
      this.cleanup = [];
      this.editorCleanup = null;
      this.activeIndex = null;
      this.initialActiveIndex = null;
      this.editorActiveBlockId = null;
      this.resetDelay = 600;
      this.resetTimer = null;
    }

    connectedCallback() {
      this.items = Array.from(this.querySelectorAll("[data-ritual-item]"));
      this.overlays = Array.from(
        this.querySelectorAll("[data-ritual-overlay]"),
      );
      this.baseLayer = this.querySelector("[data-ritual-base]");
      this.isMobileQuery = window.matchMedia("(max-width: 749px)");

      if (this.items.length === 0) {
        return;
      }

      const initiallyActive = this.items.find((item) =>
        item.classList.contains("is-active"),
      );
      this.initialActiveIndex = initiallyActive
        ? initiallyActive.dataset.ritualIndex || null
        : null;

      this.items.forEach((item) => {
        const activateItem = () => {
          if (this.editorActiveBlockId !== null) {
            return;
          }
          if (this.resetTimer) {
            window.clearTimeout(this.resetTimer);
            this.resetTimer = null;
          }
          this.setActiveByIndex(item.dataset.ritualIndex || null);
        };

        if (this.isMobileQuery.matches) {
          this.addListener(item, "keydown", (event) => {
            if (
              event.target.closest("[data-curated-collection-cta-link]") ||
              event.target.closest("[data-curated-collection-card-link]")
            ) {
              return;
            }
            if (event.key !== "Enter" && event.key !== " ") {
              return;
            }
            event.preventDefault();
            activateItem();
          });
          this.addListener(item, "click", (event) => {
            if (
              event.target.closest("[data-curated-collection-cta-link]") ||
              event.target.closest("[data-curated-collection-card-link]")
            ) {
              return;
            }
            event.preventDefault();
            activateItem();
          });
          return;
        }

        this.addListener(item, "mouseenter", activateItem);
        this.addListener(item, "focusin", activateItem);

        const deactivateItem = () => {
          if (this.editorActiveBlockId !== null) {
            return;
          }
          if (this.resetTimer) {
            window.clearTimeout(this.resetTimer);
          }
          this.resetTimer = window.setTimeout(() => {
            this.setActiveByIndex(this.initialActiveIndex);
          }, this.resetDelay);
        };

        this.addListener(item, "mouseleave", deactivateItem);
        this.addListener(item, "focusout", deactivateItem);
      });

      this.bindEditorEvents();
      this.setActiveByIndex(this.initialActiveIndex);
    }

    disconnectedCallback() {
      if (this.resetTimer) {
        window.clearTimeout(this.resetTimer);
        this.resetTimer = null;
      }
      this.editorActiveBlockId = null;
      if (this.editorCleanup) {
        this.editorCleanup();
        this.editorCleanup = null;
      }
      this.cleanup.forEach((remove) => remove());
      this.cleanup = [];
    }

    addListener(el, event, handler, options) {
      if (!el) return;
      el.addEventListener(event, handler, options);
      this.cleanup.push(() => el.removeEventListener(event, handler, options));
    }

    bindEditorEvents() {
      if (
        !window.webvista ||
        typeof window.webvista.bindShopifyEditorBlockEvents !== "function"
      ) {
        return;
      }

      this.editorCleanup = window.webvista.bindShopifyEditorBlockEvents({
        root: this,
        getBlockElement: (blockId) =>
          this.querySelector(
            `[data-ritual-item][data-block-id="${blockId}"], [data-ritual-slide][data-block-id="${blockId}"]`,
          ),
        onSelect: ({ blockId, blockElement }) => {
          const item = this.resolveItemFromBlockElement(blockElement);
          if (!item) {
            return;
          }
          this.editorActiveBlockId = blockId;
          if (this.resetTimer) {
            window.clearTimeout(this.resetTimer);
            this.resetTimer = null;
          }
          this.setActiveByIndex(item.dataset.ritualIndex || null);
        },
        onDeselect: ({ blockId }) => {
          if (!blockId || blockId !== this.editorActiveBlockId) {
            return;
          }
          this.editorActiveBlockId = null;
          window.setTimeout(() => {
            if (this.editorActiveBlockId !== null) {
              return;
            }
            this.setActiveByIndex(this.initialActiveIndex);
          }, 0);
        },
      });
    }

    resolveItemFromBlockElement(blockElement) {
      if (!blockElement) return null;
      if (blockElement.matches("[data-ritual-item]")) {
        return blockElement;
      }
      if (blockElement.matches("[data-ritual-slide]")) {
        return blockElement.querySelector("[data-ritual-item]");
      }
      return (
        blockElement.closest("[data-ritual-item]") ||
        blockElement.closest("[data-ritual-slide]")?.querySelector(
          "[data-ritual-item]",
        )
      );
    }

    setActiveByIndex(index) {
      this.activeIndex = index === undefined || index === null ? null : String(index);

      this.overlays.forEach((overlay) => {
        const overlayIndex = overlay.dataset.ritualIndex || null;
        if (this.activeIndex !== null && overlayIndex === this.activeIndex) {
          overlay.classList.add("is-active");
        } else {
          overlay.classList.remove("is-active");
        }
      });

      this.items.forEach((item) => {
        const itemIndex = item.dataset.ritualIndex || null;
        if (this.activeIndex !== null && itemIndex === this.activeIndex) {
          item.classList.add("is-active");
        } else {
          item.classList.remove("is-active");
        }
      });
    }
  }

  customElements.define("curated-collection", CuratedCollection);
})();
